import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertTriangle } from 'lucide-react'
import { usePageHeader } from '@/components/page-header-context'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { usePlatformConfig, useUpdatePlatformConfig } from '@/features/settings/use-platform-config'
import { BILLING_PERIOD_WEEKS, ROUNDING_STEPS } from '@/features/settings/settings.types'

// Bornes alignées sur le DTO API (validation côté serveur en miroir, SCRUM-80/92).
// `valueAsNumber` au register → les champs sont déjà des nombres ici.
const settingsSchema = z.object({
  ratePerKm: z.number({ message: 'Valeur requise' }).min(0.01, 'Min 0,01').max(10, 'Max 10'),
  baseFare: z.number({ message: 'Valeur requise' }).min(0, 'Min 0').max(5, 'Max 5'),
  roundingStep: z
    .number({ message: 'Valeur requise' })
    .refine((v) => (ROUNDING_STEPS as readonly number[]).includes(v), 'Palier invalide'),
  commissionPct: z.number({ message: 'Valeur requise' }).min(0, 'Min 0').max(50, 'Max 50'),
  billingPeriodWeeks: z
    .number({ message: 'Valeur requise' })
    .refine((v) => (BILLING_PERIOD_WEEKS as readonly number[]).includes(v), 'Période invalide'),
  debtCapDinar: z.number({ message: 'Valeur requise' }).min(0, 'Min 0').max(500, 'Max 500'),
  graceAfterInvoiceDays: z.number({ message: 'Valeur requise' }).min(0, 'Min 0').max(30, 'Max 30'),
})

type SettingsForm = z.infer<typeof settingsSchema>

interface FieldProps {
  label: string
  unit: string
  error?: string
  children: React.ReactNode
}

function Field({ label, unit, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-ink">
        {label} <span className="text-muted font-normal">({unit})</span>
      </label>
      {children}
      {error && <p className="text-danger text-xs">{error}</p>}
    </div>
  )
}

export default function SettingsPage() {
  usePageHeader('Paramètres', 'Paramétrage tarifaire et facturation de la plateforme')
  const { data, isLoading, isError } = usePlatformConfig()
  const update = useUpdatePlatformConfig()
  const [toastOpen, setToastOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SettingsForm>({ resolver: zodResolver(settingsSchema) })

  // Réinitialise le formulaire avec les valeurs DB dès qu'elles arrivent.
  useEffect(() => {
    if (data) {
      reset({
        ratePerKm: data.ratePerKm,
        baseFare: data.baseFare,
        roundingStep: data.roundingStep,
        commissionPct: data.commissionPct,
        billingPeriodWeeks: data.billingPeriodWeeks,
        debtCapDinar: data.debtCapDinar,
        graceAfterInvoiceDays: data.graceAfterInvoiceDays,
      })
    }
  }, [data, reset])

  const onSubmit = (form: SettingsForm) => {
    update.mutate(form, { onSuccess: () => setToastOpen(true) })
  }

  if (isLoading) {
    return <p className="text-[13px] text-muted">Chargement du paramétrage…</p>
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-danger/10 border border-danger/30">
        <AlertTriangle size={16} className="text-danger shrink-0" />
        <span className="text-[13px] font-medium text-danger">
          Paramétrage indisponible — réessayez plus tard.
        </span>
      </div>
    )
  }

  return (
    <Card className="max-w-xl p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Tarif kilométrique" unit="DT/km" error={errors.ratePerKm?.message}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max="10"
              {...register('ratePerKm', { valueAsNumber: true })}
            />
          </Field>

          <Field label="Prise en charge" unit="DT" error={errors.baseFare?.message}>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="5"
              {...register('baseFare', { valueAsNumber: true })}
            />
          </Field>

          <Field label="Palier d'arrondi" unit="DT" error={errors.roundingStep?.message}>
            <select
              {...register('roundingStep', { valueAsNumber: true })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {ROUNDING_STEPS.map((step) => (
                <option key={step} value={step}>
                  {step.toLocaleString('fr-FR')}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Commission plateforme" unit="%" error={errors.commissionPct?.message}>
            <Input
              type="number"
              step="1"
              min="0"
              max="50"
              {...register('commissionPct', { valueAsNumber: true })}
            />
          </Field>
        </div>

        <div className="border-t border-line pt-5">
          <h2 className="text-[13px] font-semibold text-ink mb-4">Facturation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="Période de facturation"
              unit="semaines"
              error={errors.billingPeriodWeeks?.message}
            >
              <select
                {...register('billingPeriodWeeks', { valueAsNumber: true })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {BILLING_PERIOD_WEEKS.map((weeks) => (
                  <option key={weeks} value={weeks}>
                    {weeks}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Plafond de dette" unit="DT" error={errors.debtCapDinar?.message}>
              <Input
                type="number"
                step="1"
                min="0"
                max="500"
                {...register('debtCapDinar', { valueAsNumber: true })}
              />
            </Field>

            <Field
              label="Délai de grâce"
              unit="jours"
              error={errors.graceAfterInvoiceDays?.message}
            >
              <Input
                type="number"
                step="1"
                min="0"
                max="30"
                {...register('graceAfterInvoiceDays', { valueAsNumber: true })}
              />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-line pt-4">
          <p className="font-mono text-[11px] text-muted">
            Dernière modif. {new Date(data.updatedAt).toLocaleString('fr-FR')}
            {data.updatedBy ? ` · ${data.updatedBy.slice(0, 8)}` : ''}
          </p>
          <Button type="submit" disabled={update.isPending || !isDirty}>
            {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>

        {update.isError && (
          <p className="text-danger text-xs">Échec de l'enregistrement — réessayez.</p>
        )}
      </form>

      <Toast
        open={toastOpen}
        onOpenChange={setToastOpen}
        title="Paramétrage enregistré"
        description="Les nouveaux tarifs s'appliquent immédiatement."
      />
    </Card>
  )
}
